import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInvalidateSetup, useRatePlans, useRoomBlocks, useRoomTypes, useRooms, useTaxRules } from '../api/queries';
import type { RatePlan, TaxRule } from '../api/types';
import { DEPARTMENTS, DEPARTMENT_LABEL, addDays, formatDate, formatGhs, parseCedis } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { HotelDetails } from './HotelDetails';

type Tab = 'rooms' | 'rates' | 'taxes' | 'hotel';

export function Setup() {
  const { can } = useAuth();
  const canInv = can('inventory.manage');
  const canRates = can('rates.manage');
  const canHotel = can('property.manage');
  const [tab, setTab] = useState<Tab>(canInv ? 'rooms' : canRates ? 'rates' : 'hotel');

  return (
    <div className="page">
      <PageHead eyebrow="Owner and manager" title="Setup" />
      <div className="seg" role="group" aria-label="Setup area">
        {canInv ? (
          <button type="button" aria-pressed={tab === 'rooms'} onClick={() => setTab('rooms')}>
            Rooms
          </button>
        ) : null}
        {canRates ? (
          <>
            <button type="button" aria-pressed={tab === 'rates'} onClick={() => setTab('rates')}>
              Rates
            </button>
            <button type="button" aria-pressed={tab === 'taxes'} onClick={() => setTab('taxes')}>
              Taxes
            </button>
          </>
        ) : null}
        {canHotel ? (
          <button type="button" aria-pressed={tab === 'hotel'} onClick={() => setTab('hotel')}>
            Hotel
          </button>
        ) : null}
      </div>
      {tab === 'hotel' && canHotel ? <HotelDetails /> : null}
      {tab === 'rooms' && canInv ? <RoomsSetup /> : null}
      {tab === 'rates' && canRates ? <RatesSetup /> : null}
      {tab === 'taxes' && canRates ? <TaxesSetup /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------- rooms */
function RoomsSetup() {
  const types = useRoomTypes();
  const rooms = useRooms();
  const inv = useInvalidateSetup();
  const toast = useToast();
  const report = useReport();
  const [typeOpen, setTypeOpen] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);

  const [name, setName] = useState('');
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(1);
  const addType = useMutation({
    mutationFn: () => api('/room-types', { method: 'POST', body: { name: name.trim(), maxAdults: adults, maxChildren: kids } }),
    onSuccess: () => {
      toast.ok('Room type added');
      setName('');
      setTypeOpen(false);
      inv();
    },
    onError: report,
  });

  const [typeId, setTypeId] = useState('');
  const [numbers, setNumbers] = useState('');
  const [floor, setFloor] = useState('');
  const numberList = numbers.split(/[,\s]+/).map((n) => n.trim()).filter(Boolean);
  const addRooms = useMutation({
    mutationFn: async () => {
      // one request per room so a duplicate number only fails that room, not the whole batch
      const failed: string[] = [];
      for (const number of numberList) {
        try {
          await api('/rooms', { method: 'POST', body: { roomTypeId: typeId, number, ...(floor.trim() ? { floor: floor.trim() } : {}) } });
        } catch {
          failed.push(number);
        }
      }
      return failed;
    },
    onSuccess: (failed) => {
      inv();
      if (failed.length) toast.fail(`Could not add: ${failed.join(', ')} (already exists?)`);
      else toast.ok(`${numberList.length} room${numberList.length === 1 ? '' : 's'} added`);
      if (!failed.length) {
        setNumbers('');
        setRoomOpen(false);
      }
    },
    onError: report,
  });

  useEffect(() => {
    if (!typeId && types.data?.[0]) setTypeId(types.data[0].id);
  }, [types.data, typeId]);

  if (types.isLoading || rooms.isLoading) return <Loading />;
  if (types.error) return <ErrorNote error={types.error} retry={() => void types.refetch()} />;

  return (
    <div className="stack">
      <div className="row">
        <Button variant="primary" onClick={() => setTypeOpen(true)}>
          Add room type
        </Button>
        <Button onClick={() => setRoomOpen(true)} disabled={!types.data?.length}>
          Add rooms
        </Button>
      </div>

      {(types.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="rooms" title="No room types yet">
            Start with a room type such as “Standard Room”, then add its rooms.
          </Empty>
        </div>
      ) : (
        (types.data ?? []).map((t) => (
          <section key={t.id} className="card" aria-label={t.name}>
            <div className="card-head">
              <h2 className="h2">{t.name}</h2>
              <Badge tone={t.active ? 'good' : ''}>{t.active ? 'Active' : 'Inactive'}</Badge>
            </div>
            <p className="small muted" style={{ marginBottom: 10 }}>
              Up to {t.maxAdults} adult{t.maxAdults === 1 ? '' : 's'} and {t.maxChildren} child{t.maxChildren === 1 ? '' : 'ren'} · {t.roomCount} room{t.roomCount === 1 ? '' : 's'}
            </p>
            <div className="row">
              {(rooms.data ?? [])
                .filter((r) => r.roomType.id === t.id)
                .map((r) => (
                  <Link key={r.id} to={`/rooms/${r.id}`} className="badge brand num" style={{ textDecoration: 'none' }} aria-label={`Room ${r.number}: see details`}>
                    {r.number}
                  </Link>
                ))}
            </div>
          </section>
        ))
      )}

      <BlocksSection />

      <Modal open={typeOpen} onClose={() => setTypeOpen(false)} title="Add a room type">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (name.trim().length >= 2) addType.mutate();
          }}
        >
          <Field id="rt-name" label="Name">
            <input id="rt-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Deluxe Room" autoFocus />
          </Field>
          <div className="grid-2">
            <Field id="rt-ad" label="Max adults">
              <input id="rt-ad" className="input" type="number" min={1} max={10} value={adults} onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))} />
            </Field>
            <Field id="rt-ch" label="Max children">
              <input id="rt-ch" className="input" type="number" min={0} max={10} value={kids} onChange={(e) => setKids(Math.max(0, Number(e.target.value) || 0))} />
            </Field>
          </div>
          <div className="modal-foot">
            <Button type="button" onClick={() => setTypeOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={name.trim().length < 2} loading={addType.isPending}>
              Add room type
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={roomOpen} onClose={() => setRoomOpen(false)} title="Add rooms">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (typeId && numberList.length) addRooms.mutate();
          }}
        >
          <Field id="ar-type" label="Room type">
            <select id="ar-type" className="select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              {(types.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field id="ar-nums" label="Room numbers" hint="Separate with commas or spaces, e.g. 101, 102, 103">
            <input id="ar-nums" className="input" value={numbers} onChange={(e) => setNumbers(e.target.value)} autoFocus />
          </Field>
          <Field id="ar-floor" label="Floor (optional)">
            <input id="ar-floor" className="input" value={floor} onChange={(e) => setFloor(e.target.value)} />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setRoomOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!numberList.length} loading={addRooms.isPending}>
              Add {numberList.length || ''} room{numberList.length === 1 ? '' : 's'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------- rates */
function RatesSetup() {
  const types = useRoomTypes();
  const plans = useRatePlans();
  const inv = useInvalidateSetup();
  const toast = useToast();
  const report = useReport();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<RatePlan | null>(null);

  const [typeId, setTypeId] = useState('');
  const [code, setCode] = useState('');
  const [planName, setPlanName] = useState('Standard rate');
  const [base, setBase] = useState('');
  const [weekend, setWeekend] = useState('');
  const [mode, setMode] = useState<'INCLUSIVE' | 'EXCLUSIVE'>('INCLUSIVE');
  const [seasons, setSeasons] = useState<SeasonDraft[]>([]);

  const basePes = parseCedis(base);
  const weekendPes = weekend.trim() === '' ? null : parseCedis(weekend);
  const valid = !!typeId && /^[A-Z0-9_-]{2,20}$/.test(code) && planName.trim().length >= 2 && basePes !== null && basePes > 0 && (weekendPes === null ? weekend.trim() === '' : weekendPes > 0);

  const seasonCheck = checkSeasons(seasons);
  const seasonBody = seasonCheck.ok ? seasonCheck.list : [];
  const planValid = valid && seasonCheck.ok;

  const save = useMutation({
    mutationFn: () =>
      edit
        ? api(`/rate-plans/${edit.id}`, { method: 'PATCH', body: { name: planName.trim(), baseRate: basePes, weekendRate: weekendPes, taxMode: mode, seasonal: seasonBody } })
        : api('/rate-plans', { method: 'POST', body: { roomTypeId: typeId, code, name: planName.trim(), baseRate: basePes, ...(weekendPes ? { weekendRate: weekendPes } : {}), taxMode: mode, seasonal: seasonBody } }),
    onSuccess: () => {
      toast.ok(edit ? 'Rate updated' : 'Rate plan added');
      setOpen(false);
      inv();
    },
    onError: report,
  });

  function openNew() {
    setEdit(null);
    setTypeId(types.data?.[0]?.id ?? '');
    setCode('');
    setPlanName('Standard rate');
    setBase('');
    setWeekend('');
    setMode('INCLUSIVE');
    setSeasons([]);
    setOpen(true);
  }
  function openEdit(p: RatePlan) {
    setEdit(p);
    setTypeId(p.roomTypeId);
    setCode(p.code);
    setPlanName(p.name);
    setBase((p.baseRate / 100).toFixed(2));
    setWeekend(p.weekendRate ? (p.weekendRate / 100).toFixed(2) : '');
    setMode(p.taxMode);
    setSeasons(p.seasonal.map((s) => ({ from: s.from, to: s.to, rate: (s.rate / 100).toFixed(2) })));
    setOpen(true);
  }

  if (plans.isLoading || types.isLoading) return <Loading />;
  if (plans.error) return <ErrorNote error={plans.error} retry={() => void plans.refetch()} />;
  const typeName = (id: string) => types.data?.find((t) => t.id === id)?.name ?? 'Room';

  return (
    <div className="stack">
      <div className="row">
        <Button variant="primary" onClick={openNew} disabled={!types.data?.length}>
          Add rate plan
        </Button>
      </div>
      {!types.data?.length ? <div className="banner">Add a room type first (Rooms tab), then set its rates here.</div> : null}
      {(plans.data ?? []).length === 0 && types.data?.length ? (
        <div className="card flat">
          <Empty icon="bookings" title="No rates yet">
            Guests cannot be booked until a room type has a rate plan.
          </Empty>
        </div>
      ) : (
        <div className="list">
          {(plans.data ?? []).map((p) => (
            <div key={p.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">
                  {typeName(p.roomTypeId)} · {p.name}
                </span>
                <Badge tone={p.active ? 'good' : ''}>{p.code}</Badge>
              </div>
              <div className="row small muted">
                <span>{formatGhs(p.baseRate)} per night</span>
                {p.weekendRate ? <span>· {formatGhs(p.weekendRate)} Fri and Sat</span> : null}
                <span>· {p.taxMode === 'INCLUSIVE' ? 'tax included' : 'tax added on top'}</span>
                {p.seasonal.length ? <span>· {p.seasonal.length} seasonal</span> : null}
              </div>
              <div>
                <Button size="small" onClick={() => openEdit(p)}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? 'Edit rate plan' : 'Add a rate plan'}>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (planValid) save.mutate();
          }}
        >
          <Field id="rp-type" label="Room type">
            <select id="rp-type" className="select" value={typeId} onChange={(e) => setTypeId(e.target.value)} disabled={!!edit}>
              {(types.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid-2">
            <Field id="rp-code" label="Code" hint="Capitals, digits, - or _" error={code && !/^[A-Z0-9_-]{2,20}$/.test(code) ? 'Use 2-20 capital letters or digits' : undefined}>
              <input id="rp-code" className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!!edit} placeholder="STD-BAR" />
            </Field>
            <Field id="rp-name" label="Name">
              <input id="rp-name" className="input" value={planName} onChange={(e) => setPlanName(e.target.value)} />
            </Field>
            <Field id="rp-base" label="Nightly rate (GH₵)" error={base && basePes === null ? 'Enter an amount like 250 or 250.00' : undefined}>
              <input id="rp-base" className="input" inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} />
            </Field>
            <Field id="rp-wk" label="Friday and Saturday (GH₵)" hint="Leave empty if the same" error={weekend.trim() && weekendPes === null ? 'Enter an amount like 300' : undefined}>
              <input id="rp-wk" className="input" inputMode="decimal" value={weekend} onChange={(e) => setWeekend(e.target.value)} />
            </Field>
          </div>
          <div className="seg" role="group" aria-label="Tax on these prices">
            <button type="button" aria-pressed={mode === 'INCLUSIVE'} onClick={() => setMode('INCLUSIVE')}>
              Prices include tax
            </button>
            <button type="button" aria-pressed={mode === 'EXCLUSIVE'} onClick={() => setMode('EXCLUSIVE')}>
              Add tax on top
            </button>
          </div>
          <SeasonEditor seasons={seasons} onChange={setSeasons} error={seasonCheck.ok ? undefined : seasonCheck.error} />
          <div className="modal-foot">
            <Button type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!planValid} loading={save.isPending}>
              {edit ? 'Save changes' : 'Add rate plan'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------ seasonal prices */
interface SeasonDraft {
  from: string;
  to: string;
  rate: string;
}

/** Checks every season and turns them into what the server expects. Overlaps are refused here and again by the server. */
function checkSeasons(list: SeasonDraft[]): { ok: true; list: { from: string; to: string; rate: number }[] } | { ok: false; error: string } {
  const out: { from: string; to: string; rate: number }[] = [];
  for (const s of list) {
    const rate = parseCedis(s.rate);
    if (!s.from || !s.to) return { ok: false, error: 'Fill in both dates for each season.' };
    if (s.from > s.to) return { ok: false, error: 'A season cannot end before it starts.' };
    if (rate === null || rate <= 0) return { ok: false, error: 'Enter a nightly price for each season, like 350 or 350.00.' };
    out.push({ from: s.from, to: s.to, rate });
  }
  const sorted = [...out].sort((a, b) => a.from.localeCompare(b.from));
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i - 1]!.to >= sorted[i]!.from) return { ok: false, error: 'Seasons must not overlap. Shorten one so they only touch.' };
  }
  return { ok: true, list: out };
}

function SeasonEditor({ seasons, onChange, error }: { seasons: SeasonDraft[]; onChange: (s: SeasonDraft[]) => void; error: string | undefined }) {
  const set = (i: number, patch: Partial<SeasonDraft>) => onChange(seasons.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="stack">
      <div className="eyebrow">Seasonal prices (optional)</div>
      <p className="small muted">A season replaces the normal and weekend price for every night inside it, for example Christmas or a festival.</p>
      {seasons.map((s, i) => (
        <div key={i} className="stack card flat" style={{ padding: 12 }}>
          <div className="grid-2">
            <Field id={`ss-from-${i}`} label="First night">
              <input id={`ss-from-${i}`} className="input" type="date" value={s.from} onChange={(e) => set(i, { from: e.target.value })} />
            </Field>
            <Field id={`ss-to-${i}`} label="Last night">
              <input id={`ss-to-${i}`} className="input" type="date" value={s.to} min={s.from} onChange={(e) => set(i, { to: e.target.value })} />
            </Field>
          </div>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <Field id={`ss-rate-${i}`} label="Nightly price (GH₵)">
              <input id={`ss-rate-${i}`} className="input" inputMode="decimal" value={s.rate} onChange={(e) => set(i, { rate: e.target.value })} />
            </Field>
            <Button type="button" variant="ghost" onClick={() => onChange(seasons.filter((_, j) => j !== i))}>
              Remove
            </Button>
          </div>
        </div>
      ))}
      {error ? (
        <div className="banner bad" role="alert">
          {error}
        </div>
      ) : null}
      <div>
        <Button type="button" onClick={() => onChange([...seasons, { from: '', to: '', rate: '' }])}>
          Add a season
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- rooms out of order */
function BlocksSection() {
  const blocks = useRoomBlocks();
  const rooms = useRooms();
  const inv = useInvalidateSetup();
  const toast = useToast();
  const report = useReport();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [from, setFrom] = useState('');
  const [back, setBack] = useState('');
  const [reason, setReason] = useState('');

  const make = useMutation({
    mutationFn: () => api(`/rooms/${roomId}/blocks`, { method: 'POST', body: { from, to: back, reason: reason.trim() } }),
    onSuccess: () => {
      toast.ok('Room taken out of order');
      setOpen(false);
      setReason('');
      inv();
    },
    onError: report,
  });
  const lift = useMutation({
    mutationFn: (id: string) => api(`/room-blocks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.ok('Room is back in service');
      inv();
    },
    onError: report,
  });

  const today = me?.property.today ?? '';
  const list = (blocks.data ?? []).filter((b) => b.to > today); // finished blocks are history
  const valid = !!roomId && !!from && !!back && back > from && reason.trim().length >= 3;

  return (
    <section className="stack" aria-label="Rooms out of order">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="eyebrow">Rooms out of order</div>
        <Button
          onClick={() => {
            setRoomId(rooms.data?.[0]?.id ?? '');
            setFrom(today);
            setBack(addDays(today, 1));
            setOpen(true);
          }}
          disabled={!rooms.data?.length}
        >
          Take a room out of order
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="small muted">No rooms are out of order. Use this for repairs, painting or anything that makes a room unsellable for a while. A room with a booking in that period cannot be blocked until the booking is moved.</p>
      ) : (
        <div className="list">
          {list.map((b) => (
            <div key={b.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">Room {b.roomNumber}</span>
                <Button variant="ghost" loading={lift.isPending && lift.variables === b.id} onClick={() => lift.mutate(b.id)}>
                  Back in service
                </Button>
              </div>
              <span className="small muted">
                {formatDate(b.from, true)} until the morning of {formatDate(b.to, true)} · {b.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Take a room out of order">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (valid) make.mutate();
          }}
        >
          <Field id="ob-room" label="Room">
            <select id="ob-room" className="select" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {(rooms.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.number} · {r.roomType.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid-2">
            <Field id="ob-from" label="Out of order from">
              <input id="ob-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field id="ob-back" label="Back in service on" hint="The morning it can be sold again" error={back && from && back <= from ? 'Must be after the start' : undefined}>
              <input id="ob-back" className="input" type="date" value={back} min={from} onChange={(e) => setBack(e.target.value)} />
            </Field>
          </div>
          <Field id="ob-why" label="Why?">
            <input id="ob-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Repainting, broken air conditioning" />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>
              Take out of order
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

/* ------------------------------------------------------------------- taxes */
function TaxesSetup() {
  const rules = useTaxRules();
  const inv = useInvalidateSetup();
  const toast = useToast();
  const report = useReport();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<TaxRule | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [pct, setPct] = useState('');
  const [depts, setDepts] = useState<string[]>([]);
  const [compound, setCompound] = useState<string[]>([]);
  const [active, setActive] = useState(true);

  const pctNum = pct.trim() === '' ? null : Number(pct);
  const pctOk = pctNum !== null && Number.isFinite(pctNum) && pctNum >= 0 && pctNum <= 100 && /^\d+(\.\d{1,2})?$/.test(pct.trim());
  const valid = /^[A-Z0-9_]{2,20}$/.test(code) && name.trim().length >= 2 && pctOk;

  const save = useMutation({
    mutationFn: () => api(`/tax-rules/${code}`, { method: 'PUT', body: { name: name.trim(), rateBps: Math.round((pctNum ?? 0) * 100), appliesTo: depts, compoundOn: compound.filter((c) => c !== code), active } }),
    onSuccess: () => {
      toast.ok('Tax rule saved');
      setOpen(false);
      inv();
    },
    onError: report,
  });

  function openRule(r: TaxRule | null) {
    setEdit(r);
    setCode(r?.code ?? '');
    setName(r?.name ?? '');
    setPct(r ? String(r.rateBps / 100) : '');
    setDepts(r?.appliesTo ?? []);
    setCompound(r?.compoundOn ?? []);
    setActive(r?.active ?? true);
    setOpen(true);
  }
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  if (rules.isLoading) return <Loading />;
  if (rules.error) return <ErrorNote error={rules.error} retry={() => void rules.refetch()} />;

  return (
    <div className="stack">
      <div className="banner info">Tax and levy rates come from the Ghana Revenue Authority and change over time. Set them together with your accountant and check them before opening.</div>
      <div className="row">
        <Button variant="primary" onClick={() => openRule(null)}>
          Add tax or levy
        </Button>
      </div>
      {(rules.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="bookings" title="No taxes set up">
            Until you add some, prices carry no tax.
          </Empty>
        </div>
      ) : (
        <div className="list">
          {(rules.data ?? []).map((r) => (
            <div key={r.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">
                  {r.name} <span className="muted num">· {r.rateBps / 100}%</span>
                </span>
                <Badge tone={r.active ? 'good' : ''}>{r.code}</Badge>
              </div>
              <div className="small muted">
                Applies to {r.appliesTo.length ? r.appliesTo.map((d) => DEPARTMENT_LABEL[d] ?? d).join(', ') : 'everything'}
                {r.compoundOn.length ? ` · charged on top of ${r.compoundOn.join(', ')}` : ''}
              </div>
              <div>
                <Button size="small" onClick={() => openRule(r)}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.code}` : 'Add a tax or levy'}>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <div className="grid-2">
            <Field id="tx-code" label="Code" hint="Capitals, digits or _" error={code && !/^[A-Z0-9_]{2,20}$/.test(code) ? 'Use 2-20 capital letters or digits' : undefined}>
              <input id="tx-code" className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!!edit} placeholder="VAT" />
            </Field>
            <Field id="tx-pct" label="Rate (%)" error={pct && !pctOk ? 'Enter a rate like 15 or 2.5' : undefined}>
              <input id="tx-pct" className="input" inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} />
            </Field>
          </div>
          <Field id="tx-name" label="Name">
            <input id="tx-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="label">Applies to (none ticked means everything)</legend>
            <div className="row" style={{ marginTop: 6 }}>
              {DEPARTMENTS.map((d) => (
                <label key={d} className="row small" style={{ cursor: 'pointer', minHeight: 36 }}>
                  <input type="checkbox" checked={depts.includes(d)} onChange={() => toggle(depts, setDepts, d)} style={{ width: 20, height: 20 }} />
                  {DEPARTMENT_LABEL[d]}
                </label>
              ))}
            </div>
          </fieldset>
          {(rules.data ?? []).filter((r) => r.code !== code).length ? (
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="label">Charged on top of other taxes (optional)</legend>
              <div className="row" style={{ marginTop: 6 }}>
                {(rules.data ?? [])
                  .filter((r) => r.code !== code)
                  .map((r) => (
                    <label key={r.code} className="row small" style={{ cursor: 'pointer', minHeight: 36 }}>
                      <input type="checkbox" checked={compound.includes(r.code)} onChange={() => toggle(compound, setCompound, r.code)} style={{ width: 20, height: 20 }} />
                      {r.code}
                    </label>
                  ))}
              </div>
            </fieldset>
          ) : null}
          <label className="row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} style={{ width: 22, height: 22 }} />
            <span>Active</span>
          </label>
          <div className="modal-foot">
            <Button type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
